import { ErrorBoundary } from "@sentry/react";
import { type FC, Suspense } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useSelectedCompany } from "../company/useSelectedCompany";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { LinkGen } from "../LinkGen";
import { AuthPage } from "../layout/AuthLayout";
import { InvitationCard } from "./InvitationCard";
import { useAuth, useInvitation } from "./useAuth";
import { useIsAuthenticated } from "./useIsAuthenticated";

export const PageAcceptInvite = () => {
	const isAuthenticated = useIsAuthenticated();
	const { token } = useParams<{ token: string }>();

	if (!token) {
		return <Navigate to={LinkGen.Register()} />;
	}

	if (!isAuthenticated) {
		return <Navigate to={LinkGen.Register(token)} />;
	}

	return (
		<AuthPage title="Accept invite">
			<InviteCard token={token} />
		</AuthPage>
	);
};

const InviteCard: FC<{ token: string }> = (props) => {
	const [_, setSelected] = useSelectedCompany();
	const { acceptInvite } = useAuth();
	const { data } = useInvitation(props.token);

	const handleAcceptInvite = async () => {
		await acceptInvite.mutateAsync(props.token);

		// pre-select company
		if (data?.companyID) {
			setSelected(data.companyID);
		}
	};

	return (
		<ErrorBoundary
			fallback={({ error }) => (
				<InvitationCard.Error error={error}>
					<Card.Footer>
						<Link to="/">Back to homepage</Link>
					</Card.Footer>
				</InvitationCard.Error>
			)}
		>
			<Suspense fallback={<InvitationCard.Suspense />}>
				<InvitationCard
					token={props.token}
					cta="Press below if you wish to accept the invite"
				>
					<Card.Footer>
						<Link to="/">Go back home</Link>
						<Button
							onClick={handleAcceptInvite}
							loading={acceptInvite.isPending}
						>
							Accept invite
						</Button>
					</Card.Footer>
				</InvitationCard>
			</Suspense>
		</ErrorBoundary>
	);
};
